import React, {useEffect, useMemo, useRef, useState} from "react";
import * as Icons from "react-feather";
import {Link} from "react-router-dom";
import {useAuth} from "store/auth";

const normalizeSearchText = (value) =>
  String(value || "")
    .replace(/ي/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase("fa");

export const buildSearchItems = (menus = []) =>
  menus
    .filter((item) => item?.allow !== false)
    .flatMap((item) => {
      const children = (item.childs || []).filter((child) => child?.allow !== false && child?.to);
      if (children.length) {
        return children.map((child) => ({
          ...child,
          group: item.title,
        }));
      }

      return item.to ? [{...item, group: ""}] : [];
    });

export const filterSearchItems = (items, query) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return items.slice(0, 6);

  return items
    .filter((item) =>
      normalizeSearchText(`${item.title || ""} ${item.group || ""}`).includes(normalizedQuery)
    )
    .slice(0, 8);
};

const SearchInput = ({inputRef, onChange, onFocus, query}) => (
  <input
    ref={inputRef}
    type="search"
    value={query}
    onChange={onChange}
    onFocus={onFocus}
    autoComplete="off"
    className="search__input form-control border-transparent placeholder-theme-13 dark:bg-dark-1"
    placeholder="جست‌وجوی بخش‌های پنل…"
    aria-label="جست‌وجوی بخش‌های پنل"
    aria-controls="panel-search-results"
  />
);

export default function Search() {
  const auth = useAuth();
  const container = useRef(null);
  const desktopInput = useRef(null);
  const mobileInput = useRef(null);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const items = useMemo(
    () => buildSearchItems(auth.userMeta?.panel_menus || []),
    [auth.userMeta]
  );
  const results = useMemo(() => filterSearchItems(items, query), [items, query]);

  useEffect(() => {
    const handleShortcut = (event) => {
      const target = event.target;
      const isTyping = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

      if (event.key === "Escape") {
        setIsOpen(false);
        desktopInput.current?.blur();
        mobileInput.current?.blur();
      }

      if (event.key === "/" && !isTyping) {
        event.preventDefault();
        setIsOpen(true);
        desktopInput.current?.focus();
      }
    };

    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (container.current && !container.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const handleChange = (event) => {
    setQuery(event.target.value);
    setIsOpen(true);
  };

  const closeSearch = () => {
    setIsOpen(false);
    setQuery("");
  };

  const openMobileSearch = () => {
    setIsOpen(true);
    window.requestAnimationFrame(() => mobileInput.current?.focus());
  };

  return (
    <div ref={container} className="intro-x relative ml-3 sm:ml-6">
      <div className="search hidden sm:block">
        <SearchInput
          inputRef={desktopInput}
          onChange={handleChange}
          onFocus={() => setIsOpen(true)}
          query={query}
        />
        <Icons.Search aria-hidden="true" className="search__icon dark:text-gray-300" />
      </div>

      <button
        aria-label="باز کردن جست‌وجوی پنل"
        aria-expanded={isOpen}
        aria-controls="panel-search-results"
        className="notification notification--light sm:hidden"
        onClick={openMobileSearch}
        type="button"
      >
        <Icons.Search aria-hidden="true" />
      </button>

      <div
        id="panel-search-results"
        className={`search-result ${isOpen ? "show" : ""}`}
      >
        <div className="search-result__content" role="region" aria-label="نتایج جست‌وجوی پنل">
          <div className="mb-4 sm:hidden">
            <SearchInput
              inputRef={mobileInput}
              onChange={handleChange}
              onFocus={() => setIsOpen(true)}
              query={query}
            />
          </div>

          <div className="search-result__content__title">دسترسی سریع</div>
          {results.length ? (
            <div className="space-y-1">
              {results.map((item) => {
                const ItemIcon = Icons[item.icon] || Icons.ArrowLeftCircle;
                return (
                  <Link
                    key={`${item.to}-${item.title}`}
                    to={item.to}
                    className="flex items-center rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-dark-2"
                    onClick={closeSearch}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-theme-1/10 text-theme-1">
                      <ItemIcon aria-hidden="true" className="h-4 w-4" />
                    </span>
                    <span className="mr-3 min-w-0">
                      <span className="block font-medium">{item.title}</span>
                      {item.group ? (
                        <span className="block truncate text-xs text-gray-500">{item.group}</span>
                      ) : null}
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-gray-500">بخشی با این عنوان پیدا نشد.</p>
          )}
          <p className="mt-3 hidden border-t border-gray-200 pt-3 text-xs text-gray-400 sm:block dark:border-dark-3">
            برای جست‌وجوی سریع، کلید / را فشار دهید.
          </p>
        </div>
      </div>
    </div>
  );
}
